import { useState, useEffect } from 'react';

const WhatsAppChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenClosed, setHasBeenClosed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showBubble, setShowBubble] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sync visibility with scroll progress (same logic as progress-wrap)
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          setIsVisible(scrollTop > 300); // Same threshold as scroll-to-top
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Check initial state
    requestAnimationFrame(() => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsVisible(scrollTop > 300);
    });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-open popup on desktop or show bubble on mobile when component becomes visible
  useEffect(() => {
    if (isVisible && !isOpen && !hasBeenClosed) {
      // Small delay to ensure smooth animation
      const timer = setTimeout(() => {
        if (isMobile) {
          // On mobile, show bubble instead of opening modal
          setShowBubble(true);
        } else {
          // On desktop, open modal as before
          setIsOpen(true);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isOpen, hasBeenClosed, isMobile]);

  const handleSend = () => {
    if (message.trim()) {
      const whatsappNumber = '94768950179'; // Replace with your WhatsApp number (with country code, no +)
      const encodedMessage = encodeURIComponent(`How can we help you\n\n${message.trim()}`);
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
      setMessage('');
      setIsOpen(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <div className="whatsapp-chat-wrapper">
        <button
          className="whatsapp-icon-button"
          onClick={() => {
            if (isOpen) {
              // If closing, mark as closed to prevent auto-opening
              setHasBeenClosed(true);
            }
            setIsOpen(!isOpen);
            setShowBubble(false); // Hide bubble when opening modal
          }}
          aria-label="Open WhatsApp chat"
        >
          <i className="fa-brands fa-whatsapp"></i>
        </button>

        {/* Message bubble for mobile */}
        {isMobile && showBubble && !isOpen && (
          <div 
            className="whatsapp-message-bubble"
            onClick={() => {
              setIsOpen(true);
              setShowBubble(false);
            }}
          >
            <div className="whatsapp-message-bubble-content">
              <p>How can we help you</p>
            </div>
            <button
              className="whatsapp-message-bubble-close"
              onClick={(e) => {
                e.stopPropagation();
                setShowBubble(false);
                setHasBeenClosed(true);
              }}
              aria-label="Close bubble"
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        )}

        {/* Full conversation modal */}
        {isOpen && (
          <div className="whatsapp-popup">
            <div className="whatsapp-popup-header">
              <div className="whatsapp-popup-header-content">
                <div className="whatsapp-popup-avatar">
                  <i className="fa-brands fa-whatsapp"></i>
                </div>
                <div className="whatsapp-popup-info">
                  <h4>Support</h4>
                  <span>Typically replies within an hour</span>
                </div>
              </div>
              <button
                className="whatsapp-popup-close"
                onClick={() => {
                  setIsOpen(false);
                  setHasBeenClosed(true);
                }}
                aria-label="Close chat"
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <div className="whatsapp-popup-body">
              <div className="whatsapp-message whatsapp-message-received">
                <p>How can we help you</p>
              </div>
            </div>
            <div className="whatsapp-popup-footer">
              <input
                type="text"
                placeholder="Type a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="whatsapp-input"
              />
              <button
                className="whatsapp-send-button"
                onClick={handleSend}
                aria-label="Send message"
              >
                <i className="fa-solid fa-paper-plane"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default WhatsAppChat;

